import { Container, Sprite, Texture, Graphics, Rectangle } from 'pixi.js';
import { Card } from '../engine/types';
import { CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS, COLORS } from './constants';

export class CardSprite extends Container {
  public card: Card | null = null;
  public isFaceUp: boolean = false;
  public isPlayable: boolean = false;
  public isSelected: boolean = false;

  private frontSprite: Sprite;
  private backSprite: Sprite;
  private highlightGlow: Graphics;
  private borderGfx: Graphics;

  public onCardPlayed?: (card: Card) => void;
  public onCardClicked?: (card: Card) => void;

  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private initialY: number = 0;

  constructor(frontTexture: Texture, backTexture: Texture) {
    super();

    // High quality rounded border & shadow
    this.highlightGlow = new Graphics();
    this.borderGfx = new Graphics();
    this.addChild(this.highlightGlow);

    this.backSprite = new Sprite(backTexture);
    this.backSprite.width = CARD_WIDTH;
    this.backSprite.height = CARD_HEIGHT;
    this.backSprite.anchor.set(0.5);

    this.frontSprite = new Sprite(frontTexture);
    this.frontSprite.width = CARD_WIDTH;
    this.frontSprite.height = CARD_HEIGHT;
    this.frontSprite.anchor.set(0.5);

    this.addChild(this.backSprite);
    this.addChild(this.frontSprite);
    this.addChild(this.borderGfx);

    this.drawBorder();
    this.setFaceUp(false);

    this.eventMode = 'static';
    this.cursor = 'default';
    this.interactiveChildren = false;
    this.hitArea = new Rectangle(-CARD_WIDTH / 2 - 4, -CARD_HEIGHT / 2 - 4, CARD_WIDTH + 8, CARD_HEIGHT + 8);

    this.setupInteractivity();
  }

  private drawBorder(): void {
    this.borderGfx.clear();
    this.borderGfx.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
    this.borderGfx.stroke({ width: 2, color: COLORS.cardBorder, alpha: 0.6 });
  }

  public setHighlight(active: boolean, color: number = COLORS.turnHighlight): void {
    this.highlightGlow.clear();
    if (active) {
      this.highlightGlow.roundRect(
        -CARD_WIDTH / 2 - 4,
        -CARD_HEIGHT / 2 - 4,
        CARD_WIDTH + 8,
        CARD_HEIGHT + 8,
        CARD_RADIUS + 3
      );
      this.highlightGlow.fill({ color, alpha: 0.45 });
    }
  }

  public setPlayable(playable: boolean): void {
    this.isPlayable = playable;
    this.cursor = playable ? 'pointer' : 'default';
    this.alpha = playable ? 1.0 : 0.68;
    this.setHighlight(playable);
  }

  public setFaceUp(faceUp: boolean): void {
    this.isFaceUp = faceUp;
    this.frontSprite.visible = faceUp;
    this.backSprite.visible = !faceUp;
  }

  public setCard(card: Card, faceTexture: Texture): void {
    this.card = card;
    this.frontSprite.texture = faceTexture;
  }

  public animateFlip(toFaceUp: boolean, durationMs: number = 250): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      const initialScaleX = this.scale.x;

      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / durationMs);

        if (progress < 0.5) {
          this.scale.x = initialScaleX * (1 - progress * 2);
        } else {
          this.setFaceUp(toFaceUp);
          this.scale.x = initialScaleX * ((progress - 0.5) * 2);
        }

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this.scale.x = initialScaleX;
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  }

  private hasTriggeredPlay: boolean = false;
  private isWobbling: boolean = false;

  public wobble(): void {
    if (this.isWobbling || this.hasTriggeredPlay) return;
    this.isWobbling = true;
    const startX = this.x;
    const startTime = performance.now();
    const duration = 240;

    const step = (now: number) => {
      const elapsed = now - startTime;
      if (elapsed >= duration) {
        this.x = startX;
        this.isWobbling = false;
        return;
      }
      const progress = elapsed / duration;
      const angle = progress * Math.PI * 6;
      this.x = startX + Math.sin(angle) * 7 * (1 - progress);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private setupInteractivity(): void {
    const triggerPlay = () => {
      if (this.hasTriggeredPlay || !this.card) return;
      if (!this.isPlayable) {
        this.wobble();
        return;
      }

      this.hasTriggeredPlay = true;
      this.isDragging = false;
      this.eventMode = 'none';
      this.cursor = 'default';
      this.setHighlight(false);

      // Immediate visual feedback: card lifts forward
      this.y = this.initialY - 24;

      if (this.onCardPlayed) {
        this.onCardPlayed(this.card);
      } else if (this.onCardClicked) {
        this.onCardClicked(this.card);
      }

      // Safety fallback: if engine rejects or delays, recover after 500ms
      setTimeout(() => {
        if (this.parent && this.hasTriggeredPlay) {
          this.hasTriggeredPlay = false;
          this.eventMode = 'static';
          this.setPlayable(this.isPlayable);
          this.y = this.initialY;
        }
      }, 500);
    };

    this.on('pointerdown', (e) => {
      if (this.hasTriggeredPlay || !this.card) return;
      if (!this.isPlayable) {
        this.wobble();
        return;
      }

      this.isDragging = true;
      this.dragStartY = e.global.y;
      this.initialY = this.y;
      this.y = this.initialY - 14;
      this.setHighlight(true, COLORS.accentGold);
    });

    this.on('globalpointermove', (e) => {
      if (!this.isDragging) return;
      const dy = e.global.y - this.dragStartY;
      if (dy < 0) {
        this.y = this.initialY + dy;
      }
    });

    const handleRelease = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      if (this.isPlayable && this.card && !this.hasTriggeredPlay) {
        triggerPlay();
      } else {
        this.y = this.initialY;
        this.setHighlight(this.isPlayable);
      }
    };

    this.on('pointerup', handleRelease);
    this.on('pointerupoutside', handleRelease);

    // Direct tap/click support
    this.on('pointertap', () => {
      if (!this.hasTriggeredPlay && this.card) {
        if (this.isPlayable) {
          triggerPlay();
        } else {
          this.wobble();
        }
      }
    });

    // Hover effect (desktop only when playable)
    this.on('pointerover', () => {
      if (this.isPlayable && !this.isDragging && !this.hasTriggeredPlay) {
        this.y = this.initialY - 14;
        this.setHighlight(true, COLORS.accentGold);
      }
    });

    this.on('pointerout', () => {
      if (!this.isDragging && !this.hasTriggeredPlay) {
        this.y = this.initialY;
        this.setHighlight(this.isPlayable);
      }
    });
  }

  public resetPosition(y: number): void {
    this.initialY = y;
    this.y = y;
    this.hasTriggeredPlay = false;
    this.eventMode = 'static';
    this.setPlayable(this.isPlayable);
  }
}
